import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { fetchReplay, type ReplayContributor } from "../api/client";
import { computeIndicators } from "../lib/indicators";
import { isBackfilledModel, signalColor, signalFor } from "../lib/decisions";
import { HelpTooltip } from "../lib/HelpTooltip";
import { TOOLTIPS } from "../lib/tooltips";
import { CandleChart } from "../lib/CandleChart";
import { INDICATOR_PALETTE, buildIndicatorLines } from "../lib/indicatorChart";

/**
 * "What the model saw" at one decision. The persisted obs_vector + names are
 * the literal floats fed to PPO.predict(). The bar/indicator charts give the
 * surrounding context the human needs to sanity-check the action.
 */

function fmtPct(v: number | undefined | null) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `${(Number(v) * 100).toFixed(2)}%`;
}
function fmtNum(v: number | undefined | null, digits = 4) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return Number(v).toFixed(digits);
}
function dirColor(action: number | undefined | null) {
  if (action === undefined || action === null || action === 0) return "#94a3b8";
  return action > 0 ? "#22c55e" : "#ef4444";
}

// INDICATOR_PALETTE is now imported from ../lib/indicatorChart so the
// per-decision replay and the per-day symbol view share one source of truth.

// One-line explanation per indicator, plus a "why the model cares" line.
// 5-min RTH bars: 12 bars ≈ 1 hour, 48 bars ≈ 4 hours, 78 bars ≈ one full session.
const INDICATOR_DOCS: Record<string, { what: string; helps: string }> = {
  ret_1: {
    what: "1-bar return — (close − previous close) ÷ previous close. The most recent 5-minute price change as a fraction.",
    helps: "Shortest-term momentum / reversal signal. Tells the model what the price just did in the last bar.",
  },
  ret_12: {
    what: "12-bar return — price change over the last hour (12 × 5-min), as a fraction.",
    helps: "Smooths out single-bar noise; captures recent intraday trend.",
  },
  ret_48: {
    what: "48-bar return — price change over roughly the last 4 hours, as a fraction.",
    helps: "Longer-term session drift. Helps the model distinguish 'choppy noise' from 'sustained move'.",
  },
  rv_12: {
    what: "Realized volatility — standard deviation of 12 consecutive 1-bar returns. A volatility measurement over the last hour.",
    helps: "High RV = wider price swings. The model can size differently in calm vs choppy regimes.",
  },
  rv_48: {
    what: "Same as rv_12 but computed over 48 bars (~4 hours).",
    helps: "Longer-window regime indicator. Smooths out short volatility spikes to give a 'background risk' read.",
  },
  atr_norm: {
    what: "Average True Range over 14 bars, divided by current close price. Includes bar-to-bar gaps, not just close-to-close.",
    helps: "Alternative volatility measure that includes gaps. Normalized so a $10 stock and a $1000 stock report comparable values — helps the model generalize across price levels.",
  },
  dist_vwap: {
    what: "Distance from VWAP — (close − VWAP) ÷ VWAP, where VWAP is the volume-weighted average price over the last 78 bars (~one full session).",
    helps: "Classic intraday reference. Far above VWAP suggests price has run up and may pull back; far below suggests potential bounce. Mean-reversion signal.",
  },
  tod_sin: {
    what: "Sine of the current minute-of-day, scaled so the 390-minute RTH day completes one full cycle.",
    helps: "Combined with tod_cos, encodes 'where in the trading day are we' as a continuous point on a circle. Lets the model learn time-of-day effects (open volatility, lunch lull, close auction) without hard-coding session boundaries.",
  },
  tod_cos: {
    what: "Cosine of the current minute-of-day, paired with tod_sin.",
    helps: "Together with tod_sin gives every minute a unique (sin, cos) coordinate. The model can pick up 'opening hour' vs 'late afternoon' patterns implicitly.",
  },
};

// (INDICATOR_PANE + buildIndicatorLines moved to ../lib/indicatorChart.)

export function ReplayPage() {
  const { symbol = "", decisionTs = "" } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["replay", symbol, decisionTs],
    queryFn: () => fetchReplay(symbol, decisionTs, 78),
    enabled: !!symbol && !!decisionTs,
  });

  const indicators = useMemo(() => (data ? computeIndicators(data.bars) : undefined), [data]);
  const indicatorLines = useMemo(() => buildIndicatorLines(indicators), [indicators]);

  if (isLoading) return <div>Loading replay…</div>;
  if (error) return <div className="card" style={{ color: "#fca5a5" }}>{String(error)}</div>;
  if (!data) return <div>No data</div>;

  const decisionPrice = data.current_price ?? null;
  const nextClose = data.next_close ?? null;
  const ensembleAction = data.ensemble_action ?? null;
  const decisionTsForChart = data.decision_ts;
  const signal = signalFor(data.signal_type, ensembleAction);
  const allBackfilled = data.contributors.length > 0 && data.contributors.every((c) => isBackfilledModel(c.model_id));

  return (
    <div>
      <Link to={`/ensemble?symbol=${encodeURIComponent(symbol)}`} className="backlink">
        ← {symbol} ensemble
      </Link>
      <h2 style={{ marginTop: 12 }}>
        Replay: <code>{symbol}</code> at <code>{decisionTs}</code>
      </h2>

      {allBackfilled && (
        <div className="card" style={{ background: "#1f2937", borderLeft: "3px solid #f59e0b" }}>
          <strong style={{ color: "#fbbf24" }}>Backfilled decision</strong>
          <div style={{ fontSize: 13, color: "#cbd5e1", marginTop: 6 }}>
            This row was reconstructed from <code>DecisionLog</code> by
            <code> scripts/backfill_observability.py</code> because per-model attribution wasn't
            persisted at decision time. Only the aggregated ensemble action is known — the
            individual model breakdown below is a single synthetic <code>BACKFILL-*</code>
            entry, not the real contributors. Decisions made after the observability deploy
            include the full per-model vector.
          </div>
        </div>
      )}

      <div className="card">
        <div className="kv">
          <div><HelpTooltip text={TOOLTIPS.replaySignal}><strong>Signal</strong></HelpTooltip><span style={{ color: signalColor(signal), fontWeight: 600 }}>
            {signal}{data.signal_type && data.signal_type !== signal ? ` (${data.signal_type})` : ""}
          </span></div>
          <div><HelpTooltip text={TOOLTIPS.replayEnsembleAction}><strong>Ensemble action</strong></HelpTooltip><span style={{ color: dirColor(ensembleAction) }}>
            {fmtNum(ensembleAction)}
          </span></div>
          <div><HelpTooltip text={TOOLTIPS.replayDecisionPrice}><strong>Decision price</strong></HelpTooltip><span>{fmtNum(decisionPrice, 2)}</span></div>
          <div><HelpTooltip text={TOOLTIPS.replayNextClose}><strong>Next close</strong></HelpTooltip><span>{fmtNum(nextClose, 2)}</span></div>
          <div><HelpTooltip text={TOOLTIPS.replayRealizedPnl}><strong>Realized P&L</strong></HelpTooltip><span className={data.pnl_pct === undefined ? "" : Number(data.pnl_pct) > 0 ? "pos" : "neg"}>
            {fmtPct(data.pnl_pct)}
          </span></div>
          <div><HelpTooltip text={TOOLTIPS.replayStdDev}><strong>Std dev</strong></HelpTooltip><span>{fmtNum(data.std_dev)}</span></div>
          <div><HelpTooltip text={TOOLTIPS.replayConfidence}><strong>Confidence</strong></HelpTooltip><span>{fmtNum(data.confidence, 3)}</span></div>
          <div><HelpTooltip text={TOOLTIPS.replayModels}><strong>Models</strong></HelpTooltip><span>{data.num_models ?? data.contributors.length}</span></div>
          <div><HelpTooltip text={TOOLTIPS.replayMethod}><strong>Method</strong></HelpTooltip><span>{data.method ?? "—"}</span></div>
          {data.no_order_reason && (
            <div><HelpTooltip text={TOOLTIPS.replayNoOrderReason}><strong>No-order reason</strong></HelpTooltip><span style={{ color: "#94a3b8" }}>{data.no_order_reason}</span></div>
          )}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Price candles + indicator trajectories</h3>
        <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 0 }}>
          Candles on top (price). Middle pane: returns, realized volatility,
          ATR, and VWAP distance — all on a comparable ±0.05 scale. Bottom pane:
          time-of-day sin/cos waves (isolated because their ±1 range was
          flattening every other indicator). Drag any divider to resize.
          Decision moment marked with a circle on the candle pane.
        </p>
        {data.bars.length > 0 ? (
          <CandleChart
            bars={data.bars}
            indicators={indicatorLines}
            markers={[
              {
                time: decisionTsForChart,
                price: decisionPrice ?? undefined,
                color: dirColor(ensembleAction),
                text: "decision",
                position: "aboveBar",
                shape: "circle",
              },
            ]}
            height={640}
          />
        ) : (
          <div style={{ color: "#94a3b8" }}>No bars returned by broker for this window.</div>
        )}
      </div>

      <IndicatorLegend />

      <ObsTable contributors={data.contributors} />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Per-model actions</h3>
        <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Model</th>
              <th className="num">Individual action</th>
              <th className="num">Direction</th>
            </tr>
          </thead>
          <tbody>
            {data.contributors.map((c) => (
              <tr key={c.model_id}>
                <td><code>{c.model_id}</code></td>
                <td className="num" style={{ color: dirColor(c.individual_action) }}>
                  {fmtNum(c.individual_action)}
                </td>
                <td className="num">
                  {c.individual_action === undefined || c.individual_action === 0
                    ? "flat"
                    : c.individual_action > 0
                    ? "long"
                    : "short"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function IndicatorLegend() {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>What each indicator means</h3>
      <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 0, marginBottom: 12 }}>
        These are the technical features computed from the bar series. Not all
        models use every one — each model's <code>actual_features</code> list (in
        TradingModels) controls which subset is fed to PPO. Bars are 5 minutes
        wide; 12 bars ≈ 1 hour, 48 bars ≈ 4 hours, 78 bars ≈ one full RTH session.
      </p>
      <div style={{ display: "grid", gap: 12 }}>
        {Object.entries(INDICATOR_PALETTE).map(([key, color]) => {
          const doc = INDICATOR_DOCS[key];
          if (!doc) return null;
          return (
            <div
              key={key}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 120px 1fr",
                gap: 12,
                alignItems: "start",
                paddingBottom: 8,
                borderBottom: "1px solid #1e293b",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: "inline-block",
                  width: 28,
                  height: 3,
                  background: color,
                  borderRadius: 2,
                  marginTop: 8,
                }}
              />
              <code style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 600 }}>{key}</code>
              <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                <div style={{ color: "#e2e8f0" }}>{doc.what}</div>
                <div style={{ color: "#94a3b8", marginTop: 2 }}>
                  <em>Helps:</em> {doc.helps}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ObsTable({ contributors }: { contributors: ReplayContributor[] }) {
  // All models in an ensemble see the same obs vector — show the first row's vector.
  const first = contributors.find((c) => c.obs_vector && c.obs_feature_names);
  if (!first || !first.obs_vector || !first.obs_feature_names) {
    return (
      <div className="card">
        <h3 style={{ marginTop: 0 }}>What the model saw</h3>
        <div style={{ color: "#94a3b8" }}>
          No obs_vector persisted for this decision yet. New decisions after the
          observability deploy will include the full vector.
        </div>
      </div>
    );
  }
  const obs = first.obs_vector;
  const names = first.obs_feature_names;
  // The first three entries are [cash, price, shares] — label them so the table
  // is self-explanatory; the rest are technical features.
  const labels = ["cash", "price", "shares", ...names];
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>What the model saw (obs vector)</h3>
      <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 0 }}>
        Exact floats handed to <code>PPO.predict()</code> at decision time.
      </p>
      <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Index</th>
            <th>Feature</th>
            <th className="num">Value</th>
          </tr>
        </thead>
        <tbody>
          {obs.map((v, i) => (
            <tr key={`${i}-${labels[i] ?? "?"}`}>
              <td style={{ fontSize: 12, color: "#94a3b8" }}>{i}</td>
              <td><code>{labels[i] ?? `feat_${i}`}</code></td>
              <td className="num">{fmtNum(v, 6)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}
